import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col, Tooltip } from 'antd';
import { partnerTypes } from 'utils/app-configs';
import { FormItem, Input, Button, Flex } from 'ui';
import { injectIntl } from 'react-intl';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';
// eslint-disable-next-line
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';

const EditSpan = styled.span`
  margin-left: auto;
  user-select: none;
`;

const EditText = styled.span`
  color: ${p => p.theme.colors.theme};
  text-decoration: underline;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  font-weight: bold;
  user-select: none;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: bold;
`;

const FirstRow = styled.span`
  display: flex;
  align-items: center;
`;

const EditForm = styled.div`
  width: 100%;
`;

const StyledFormItem = styled(FormItem)`
  .ant-legacy-form-item-required::before {
    display: none;
  }
  label.ant-legacy-form-item-required::after {
    content: '*';
    display: inline-block;
    margin-left: 4px;
    color: #ff4d4f;
    font-size: 14px;
    line-height: 1;
  }
`;

const StyledText = styled.p`
  font-size: 16px;
`;

const PartnerInfo = ({
  editModeEnabled,
  form,
  selectedPartner,
  intl,
  typeName,
  isSaving,
  savePartnerDetailsRequest,
  type,
  entityId,
  renderLine,
  canEditPartner,
  setEditModeEnabled,
  showEditPartnerRegion,
}) => {
  const checkIfFormValid = () => {
    if (
      form.getFieldValue('description') &&
      form.getFieldValue('city') &&
      form.getFieldValue('street') &&
      form.getFieldValue('postalCode')
    ) {
      return true;
    }
    return false;
  };

  const savePartnerDetails = () => {
    if (checkIfFormValid()) {
      savePartnerDetailsRequest({
        number: selectedPartner?.number,
        description: form.getFieldValue('description'),
        street: form.getFieldValue('street'),
        city: form.getFieldValue('city'),
        houseNumber: form.getFieldValue('houseNumber'),
        postalCode: form.getFieldValue('postalCode'),
        regionKey: showEditPartnerRegion ?
          form.getFieldValue('region') :
          null,
        partnerFunction: type,
        query: type === partnerTypes.SHIPTO ? 'shipToEdit' : '*',
        entityId,
        guid: selectedPartner?.guid,
      });
    }
  };

  const handleEditModeClick = () => {
    setEditModeEnabled(s => !s);
  };

  const descriptionValidator = form.getFieldDecorator('description', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.DESCRIPTION_GENERAL' }) }],
    initialValue: selectedPartner?.isLastUsed ?
      selectedPartner.partnerDetails?.name :
      selectedPartner?.name,
  });
  const streetValidator = form.getFieldDecorator('street', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.STREET' }) }],
    initialValue: selectedPartner?.isLastUsed ?
      selectedPartner.partnerDetails?.street :
      selectedPartner?.street,
  });
  const numberValidator = form.getFieldDecorator('houseNumber', {
    initialValue: selectedPartner?.isLastUsed ?
      selectedPartner.partnerDetails?.houseNumber :
      selectedPartner?.houseNumber,
  });
  const postalCodeValidator = form.getFieldDecorator('postalCode', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.POSTAL_CODE' }) }],
    initialValue: selectedPartner?.isLastUsed ?
      selectedPartner.partnerDetails?.postalCode :
      selectedPartner?.postalCode,
  });
  const cityValidator = form.getFieldDecorator('city', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.CITY' }) }],
    initialValue: selectedPartner?.isLastUsed ?
      selectedPartner.partnerDetails?.city :
      selectedPartner?.city,
  });
  const regionValidator = form.getFieldDecorator('region', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.REGION' }) }],
    initialValue: selectedPartner?.isLastUsed ?
      selectedPartner.partnerDetails?.regionKey :
      selectedPartner?.region,
  });
  const countryKey = selectedPartner?.isLastUsed ?
    selectedPartner.partnerDetails?.country :
    selectedPartner?.countryKey;


  const renderPartnerEditForm = () => <>
    <FirstRow>
      <Title>
        {`${intl.formatMessage({ id: 'partners.TITLE_EDIT' })} ${typeName?.toUpperCase()}`}
      </Title>
    </FirstRow>
    <EditForm>
      <Row gutter={16}>
        <Col span={24}>
          <StyledFormItem label={intl.formatMessage({ id: 'partners.DESCRIPTION' })}>
            {descriptionValidator(
              <Input disabled={isSaving} />,
            )}
          </StyledFormItem>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={18}>
          <StyledFormItem label={intl.formatMessage({ id: 'partners.STREET' })}>
            {streetValidator(
              <Input disabled={isSaving} />,
            )}
          </StyledFormItem>
        </Col>
        <Col span={6}>
          <FormItem label={intl.formatMessage({ id: 'partners.HOUSE_NUMBER' })}>
            {numberValidator(
              <Input disabled={isSaving} />,
            )}
          </FormItem>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <StyledFormItem label={intl.formatMessage({ id: 'partners.POSTAL_CODE' })}>
            {postalCodeValidator(
              <Input disabled={isSaving} />,
            )}
          </StyledFormItem>
        </Col>
        <Col span={16}>
          <StyledFormItem label={intl.formatMessage({ id: 'partners.CITY' })}>
            {cityValidator(
              <Input disabled={isSaving} />,
            )}
          </StyledFormItem>
        </Col>
      </Row>
      {showEditPartnerRegion &&
        <Row gutter={16}>
          <Col span={24}>
            <StyledFormItem label={intl.formatMessage({ id: 'partners.REGION' })}>
              {regionValidator(
                <ReferenceDataDropdown
                  endpoint={`refdata/lookup/country?iso=${countryKey}`}
                  disabled={isSaving}
                  type='installedat'
                  name={`regions_${countryKey}`}
                  valueLabel='name'
                  valueKey='id'
                  valueProperty='regions'
                />,
              )}
            </StyledFormItem>
          </Col>
        </Row>
      }
    </EditForm>
    <Flex gap={10} marginTop={10}>
      <Button
        onClick={!isSaving && handleEditModeClick}
        disabled={isSaving}
      >
        {intl.formatMessage({ id: 'partners.CANCEL' })}
      </Button>
      <Tooltip
        title={intl.formatMessage({ id: 'partners.SAVE_AS_LAST_USED' })}
        mouseEnterDelay={0.75}
      >
        <Button
          type='secondary'
          onClick={!isSaving && savePartnerDetails}
          disabled={isSaving || !checkIfFormValid()}
        >
          {intl.formatMessage({ id: 'partners.SAVE' })}
        </Button>
      </Tooltip>
    </Flex>
  </>;

  const renderPartnerDetails = () => {
    if (selectedPartner.isLastUsed) {
      const {
        workcenterId,
        number,
        partnerDetails: {
          postalCode,
          city,
          street,
          houseNumber,
          regionName,
          name,
          countryName,
          country,
        },
      } = selectedPartner;
      return <>
        <FirstRow>
          <StyledText>{`${name} (${number})`}</StyledText>
          {canEditPartner && isToggleEnabled('canEditPartner') ?
            <EditSpan>
              <EditText onClick={handleEditModeClick}>
                {intl.formatMessage({ id: 'partners.TITLE_EDIT' })}
              </EditText>
            </EditSpan> :
            null}
        </FirstRow>
        <StyledText>{workcenterId}</StyledText>
        <StyledText>{`${street} ${houseNumber}`}</StyledText>
        <StyledText>{`${postalCode} ${city}`}</StyledText>
        <StyledText>{`${regionName || ''} ${countryName || country || ''}`}</StyledText>
      </>;
    }
    return <>
      <FirstRow>
        {renderLine(selectedPartner.line2, selectedPartner.line2Icon)}
        {canEditPartner && isToggleEnabled('canEditPartner') ?
          <EditSpan>
            <EditText onClick={handleEditModeClick}>
              {intl.formatMessage({ id: 'partners.TITLE_EDIT' })}
            </EditText>
          </EditSpan> :
          null}
      </FirstRow>
      {renderLine(selectedPartner.line3, selectedPartner.line3Icon)}
      {renderLine(selectedPartner.line4, selectedPartner.line4Icon)}
      {renderLine(selectedPartner.line5, selectedPartner.line5Icon)}
      {renderLine(selectedPartner.line6, selectedPartner.line6Icon)}
    </>;
  };

  return !editModeEnabled ?
    renderPartnerDetails() :
    renderPartnerEditForm();
};

PartnerInfo.propTypes = {
  editModeEnabled: PropTypes.bool,
  form: PropTypes.object,
  selectedPartner: PropTypes.object,
  intl: PropTypes.object,
  typeName: PropTypes.string,
  isSaving: PropTypes.bool,
  savePartnerDetailsRequest: PropTypes.func,
  type: PropTypes.string,
  entityId: PropTypes.string,
  renderLine: PropTypes.func,
  canEditPartner: PropTypes.bool,
  setEditModeEnabled: PropTypes.func,
  showEditPartnerRegion: PropTypes.bool,
};

export default injectIntl(PartnerInfo);
